/**
 * 通用异步任务服务
 * 复用 videoService.js 的轮询模式，用于图片生成、剧本分析等异步任务
 */

import { authenticatedFetch } from '../utils/api';
import { API_HOST } from '../utils/constants';
import { resolveProjectStyleAnchorForTask } from '../utils/projectRuntime';
import { getCurrentImageProvider } from '../utils/wanxImageCapabilities';
import { isDatastartImageProvider, normalizeDatastartImageTaskPayload } from '../constants/datastartImageModels';
import { prepareDatastartMaskPayload } from '../utils/datastartMask';
import {
  DREAMFLY_IMAGE_MODELS,
  DREAMFLY_IMAGE_PROVIDER,
  DREAMFLY_LLM_MODEL_STORAGE_KEY,
  DREAMFLY_LLM_PROVIDER_ID,
  isDreamflyImageApiModel,
  normalizeDreamflyImageModelId,
} from '../constants/dreamflyModels';

/**
 * 提交异步任务
 * @param {string} taskType - 任务类型
 * @param {Object} payload - 任务参数
 * @param {Object} options - 额外选项 (provider, styleAnchor, shotId 等)
 * @returns {Promise<{taskId: string, taskType: string, status: string}>}
 */
export const submitTask = async (taskType, payload, options = {}) => {
  const projectId = options.projectId || payload?.projectId;
  const resolvedStyleAnchor = resolveProjectStyleAnchorForTask(projectId, options.styleAnchor);
  const requestedProvider = options.provider
    || payload?.provider
    || (typeof window !== 'undefined' ? window.localStorage?.getItem('ai_provider') : '');
  // An async task can carry two independent routes: the top-level provider is
  // usually the text model, while workflow payloads carry their image route in
  // imageProvider/imageModel.  Keep those routes separate so selecting a
  // Dreamfly image model never replaces the workflow's text provider.
  const payloadDreamflyImageModelId = normalizeDreamflyImageModelId(payload?.imageModel)
    || normalizeDreamflyImageModelId(payload?.imageProvider);
  const providerDreamflyImageModelId = normalizeDreamflyImageModelId(requestedProvider);
  const dreamflyImageModelId = payloadDreamflyImageModelId || providerDreamflyImageModelId;
  const dreamflyImageModel = DREAMFLY_IMAGE_MODELS.find(({ id }) => id === dreamflyImageModelId);
  const requestProvider = providerDreamflyImageModelId ? DREAMFLY_IMAGE_PROVIDER : requestedProvider;
  if (String(requestProvider || '').trim().toLowerCase() === DREAMFLY_LLM_PROVIDER_ID) {
    const selectedModel = typeof window !== 'undefined'
      ? String(window.localStorage?.getItem(DREAMFLY_LLM_MODEL_STORAGE_KEY) || '').trim()
      : '';
    if (!selectedModel || isDreamflyImageApiModel(selectedModel)) {
      throw new Error('请先选择 Dreamfly 文字子模型');
    }
  }
  const taskPayload = dreamflyImageModel
    ? (() => {
      const payloadWithoutQuality = { ...(payload || {}) };
      delete payloadWithoutQuality.quality;
      return {
        ...payloadWithoutQuality,
        ...(payloadDreamflyImageModelId ? { imageProvider: DREAMFLY_IMAGE_PROVIDER } : {}),
        imageModel: dreamflyImageModel.model,
        ...(dreamflyImageModel.model === 'gpt-image-2' ? { quality: 'medium' } : {}),
      };
    })()
    : payload;
  const datastartProvider = isDatastartImageProvider(requestProvider) ? requestProvider : taskPayload?.imageProvider;
  const preparedPayload = await prepareDatastartMaskPayload(taskPayload, datastartProvider);
  const body = {
    taskType,
    payload: normalizeDatastartImageTaskPayload(preparedPayload,
      datastartProvider,
      {
        validateReferences: isDatastartImageProvider(requestProvider),
        // 混合工作流/故事板的画幅还用于后续视频；原生图尺寸留给图片适配器映射。
        normalizeAspectRatio: isDatastartImageProvider(requestProvider) && !taskType.startsWith('EPISODE_STORYBOARD_BOARD_'),
      }),
  };

  // 允许前端显式覆盖 provider / styleAnchor / shotId
  if (requestProvider) body.provider = requestProvider;
  if (resolvedStyleAnchor) body.styleAnchor = resolvedStyleAnchor;
  if (options.shotId) body.shotId = options.shotId; // 新增：用于图片任务持久化
  if (options.projectId) body.projectId = options.projectId;
  if (options.episodeId) body.episodeId = options.episodeId;
  if (options.source) body.source = options.source;
  if (options.requestId) body.requestId = options.requestId;

  const headers = { 'Content-Type': 'application/json' };
  if (requestProvider) {
    headers['X-Ai-Provider'] = requestProvider;
  }
  if (resolvedStyleAnchor) {
    headers['X-Style-Anchor'] = resolvedStyleAnchor;
  }

  const response = await authenticatedFetch(`${API_HOST}/api/task/submit`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  // response 已经被 authenticatedFetch 解包为 data 部分: { taskId, taskType, status }
  if (!response || !response.taskId) {
    throw new Error('后端未返回任务ID');
  }

  return response;
};

/** 停止后端大纲/资产拆分；与 AbortController 仅停止页面查询不同。 */
export const cancelOutlineTask = async (taskId) => {
  if (!String(taskId || '').trim()) throw new Error('缺少需要停止的任务 ID');
  return authenticatedFetch(`${API_HOST}/api/task/${encodeURIComponent(taskId)}/cancel`, {
    method: 'POST',
  });
};

const TASK_RESULT_DIRECT_URL_KEYS = [
  'imageUrl',
  'image_url',
  'url',
  'resultUrl',
  'result_url',
  'outputUrl',
  'output_url',
  'generatedImage',
  'generated_image',
  'thumbnailUrl',
  'thumbnail_url',
  'videoUrl',
  'video_url',
  'audioUrl',
  'audio_url',
];

const TASK_RESULT_CONTAINER_KEYS = [
  'result',
  'data',
  'images',
  'imageUrls',
  'image_urls',
  'gridImages',
  'grid_images',
  'results',
  'outputs',
  'output',
  'files',
  'value',
];

const MANAGED_TASK_RESULT_IDENTITY_KEYS = [
  'taskType',
  'internalTaskId',
  'providerTaskId',
  'resultPayload',
  'resourceId',
];

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value, key);

const isTaskResultRecord = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

const isUsableTaskResultUrl = (value) => {
  if (typeof value !== 'string') return false;
  const text = value.trim();
  return /^(?:https?:)?\/\//i.test(text)
    || text.startsWith('/')
    || text.startsWith('blob:')
    || /^data:image\//i.test(text);
};

const parseTaskResultPayload = (payload) => {
  if (typeof payload !== 'string') return payload;
  const text = payload.trim();
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return isUsableTaskResultUrl(text) ? text : undefined;
  }
};

const looksLikeManagedTaskResult = (result) => (
  isTaskResultRecord(result)
  && MANAGED_TASK_RESULT_IDENTITY_KEYS.some((key) => hasOwn(result, key) && result[key] != null)
);

const hasDirectTaskResultUrl = (result) => (
  isTaskResultRecord(result)
  && TASK_RESULT_DIRECT_URL_KEYS.some((key) => isUsableTaskResultUrl(result[key]))
);

const unwrapTaskResultInternal = (result, seen) => {
  if (!isTaskResultRecord(result)) return result;
  if (seen.has(result)) return result;
  seen.add(result);

  if (!looksLikeManagedTaskResult(result)) return result;

  if (hasOwn(result, 'result') && result.result != null && result.result !== result) {
    return unwrapTaskResultInternal(result.result, seen);
  }

  // Persistent task recovery may only retain resultUrl plus lifecycle metadata.
  // Keep that record intact so canonicalization can add a stable imageUrl alias.
  if (hasDirectTaskResultUrl(result)) return result;

  // A non-object result is flattened into `value` by the persistent task adapter.
  if (hasOwn(result, 'value') && result.value != null && result.value !== result) {
    return unwrapTaskResultInternal(result.value, seen);
  }

  // Older records sometimes stored { result: ... } inside resultPayload. Do not
  // replace a flattened domain object with generic metadata when no result key exists.
  const parsedPayload = parseTaskResultPayload(result.resultPayload);
  if (isTaskResultRecord(parsedPayload) && hasOwn(parsedPayload, 'result') && parsedPayload.result != null) {
    return unwrapTaskResultInternal(parsedPayload.result, seen);
  }
  if (parsedPayload != null && !isTaskResultRecord(parsedPayload)) {
    return parsedPayload;
  }

  return result;
};

/**
 * Removes the lifecycle envelope used by persisted/recovered tasks while keeping
 * ordinary domain result objects intact.
 */
export const unwrapTaskResult = (result) => unwrapTaskResultInternal(result, new WeakSet());

const extractTaskResultUrlInternal = (result, seen) => {
  if (result == null) return null;
  if (typeof result === 'string') {
    const text = result.trim();
    return isUsableTaskResultUrl(text) ? text : null;
  }
  if (typeof result !== 'object') return null;
  if (seen.has(result)) return null;
  seen.add(result);

  const unwrapped = unwrapTaskResult(result);
  if (unwrapped !== result) {
    const unwrappedUrl = extractTaskResultUrlInternal(unwrapped, seen);
    if (unwrappedUrl) return unwrappedUrl;
  }

  if (Array.isArray(result)) {
    for (const item of result) {
      const url = extractTaskResultUrlInternal(item, seen);
      if (url) return url;
    }
    return null;
  }

  for (const key of TASK_RESULT_DIRECT_URL_KEYS) {
    const url = extractTaskResultUrlInternal(result[key], seen);
    if (url) return url;
  }

  for (const key of TASK_RESULT_CONTAINER_KEYS) {
    const url = extractTaskResultUrlInternal(result[key], seen);
    if (url) return url;
  }

  const parsedPayload = parseTaskResultPayload(result.resultPayload);
  if (parsedPayload !== undefined && parsedPayload !== result.resultPayload) {
    return extractTaskResultUrlInternal(parsedPayload, seen);
  }
  return null;
};

/**
 * Extracts the first generated media URL from live results and persisted task
 * envelopes. Known result containers are traversed recursively; unrelated object
 * fields are intentionally ignored so reference/input URLs cannot win by accident.
 */
export const extractTaskResultUrl = (result) => extractTaskResultUrlInternal(result, new WeakSet());

const extractTaskResultUrlsInternal = (result, ancestors) => {
  if (typeof result === 'string') {
    return isUsableTaskResultUrl(result) ? [result.trim()] : [];
  }
  if (result === null || typeof result !== 'object' || ancestors.has(result)) return [];
  ancestors.add(result);
  try {
    if (Array.isArray(result)) {
      return result.flatMap((item) => extractTaskResultUrlsInternal(item, ancestors));
    }

    const urls = TASK_RESULT_CONTAINER_KEYS.flatMap((key) => (
      extractTaskResultUrlsInternal(result[key], ancestors)
    ));
    const parsedPayload = parseTaskResultPayload(result.resultPayload);
    if (parsedPayload !== undefined) {
      urls.push(...extractTaskResultUrlsInternal(parsedPayload, ancestors));
    }
    // Canonical imageUrl/resultUrl and thumbnails are aliases, not extra images.
    // Preserve collection order instead of prepending a possibly stale first URL.
    if (urls.length > 0) return urls;

    for (const key of TASK_RESULT_DIRECT_URL_KEYS) {
      if (isUsableTaskResultUrl(result[key])) return [result[key].trim()];
    }
    return [];
  } finally {
    ancestors.delete(result);
  }
};

/**
 * Returns all generated output URLs, ordered and deduplicated. Only recognized
 * result containers are read; request/input/reference fields are never traversed.
 * This is a pure result adapter: it does not submit or replay generation tasks.
 * The single-result API stays unchanged for existing callers.
 */
export const extractTaskResultUrls = (result) => (
  [...new Set(extractTaskResultUrlsInternal(result, new WeakSet()))]
);

/**
 * Produces the one result shape consumed by both Promise and callback polling.
 * Objects keep their domain fields (gridImages, results, etc.) and receive stable
 * `url` / `imageUrl` aliases; primitive live results remain primitives.
 */
export const canonicalizeTaskResult = (rawResult) => {
  const result = unwrapTaskResult(rawResult);
  const url = extractTaskResultUrl(result) || extractTaskResultUrl(rawResult);
  if (!url || !isTaskResultRecord(result)) return result;

  const patch = {};
  if (!isUsableTaskResultUrl(result.url)) patch.url = url;
  const hasExplicitNonImageUrl = isUsableTaskResultUrl(result.videoUrl)
    || isUsableTaskResultUrl(result.video_url)
    || isUsableTaskResultUrl(result.audioUrl)
    || isUsableTaskResultUrl(result.audio_url);
  if (!hasExplicitNonImageUrl && !isUsableTaskResultUrl(result.imageUrl)) {
    patch.imageUrl = url;
  }
  return Object.keys(patch).length > 0 ? { ...result, ...patch } : result;
};

export const normalizeTaskCompletionResult = (rawResult) => {
  const result = canonicalizeTaskResult(rawResult);
  return {
    result,
    rawResult,
    url: extractTaskResultUrl(result) || extractTaskResultUrl(rawResult),
  };
};

const taskStatusRawResult = (status = {}) => (
  status.resultPayload != null
    ? { result: status.result, resultPayload: status.resultPayload }
    : status.result
);

export const createTerminalTaskError = (status = {}, task = {}) => {
  const completion = normalizeTaskCompletionResult(taskStatusRawResult(status));
  const taskId = task.taskId || status.taskId;
  const error = new Error(status.errorMessage || status.message || `任务${status.status || '失败'}：${taskId || ''}`);
  error.taskId = taskId;
  error.internalTaskId = task.internalTaskId || taskId;
  error.taskStatus = status.status;
  error.result = completion.result;
  error.rawResult = completion.rawResult;
  error.resultUrl = completion.url;
  error.recoverableUrl = completion.url;
  return error;
};

export const extractRecoverableTaskResultUrl = (error) => (
  error?.recoverableUrl
  || error?.resultUrl
  || extractTaskResultUrl(error?.result)
  || extractTaskResultUrl(error?.rawResult)
  || null
);

const normalizePollingTask = (taskOrId, options = {}) => {
  if (taskOrId && typeof taskOrId === 'object') {
    return {
      ...taskOrId,
      taskId: taskOrId.taskId,
      internalTaskId: taskOrId.internalTaskId || options.internalTaskId || taskOrId.taskId,
    };
  }
  return {
    taskId: taskOrId,
    internalTaskId: options.internalTaskId || taskOrId,
    taskType: options.taskType,
    status: 'processing',
  };
};

const withTaskIdentity = (value, task) => ({
  ...(value && typeof value === 'object' ? value : {}),
  taskId: value?.taskId || task.taskId,
  internalTaskId: value?.internalTaskId || task.internalTaskId || task.taskId,
  submittedTask: task,
});

const makeTaskAbortError = () => {
  const error = new Error('任务轮询已取消');
  error.name = 'AbortError';
  return error;
};

const throwIfTaskAborted = (signal) => {
  if (signal?.aborted) throw makeTaskAbortError();
};

const makeSubmissionUncertainError = (requestId, originalError) => {
  const error = new Error(`提交响应未能确认，将使用原请求号续查，不会重复提交：${originalError?.message || "网络异常"}`);
  error.taskId = requestId;
  error.internalTaskId = requestId;
  error.pollingInterrupted = true;
  error.submissionUncertain = true;
  error.cause = originalError;
  return error;
};

/**
 * 为一次昂贵的任务提交生成稳定业务请求号。
 * 同一次提交的请求号应在 POST 前持久化，返回丢失时继续使用它恢复原任务。
 */
export const createTaskRequestId = (scope = 'task') => {
  const safeScope = String(scope || 'task')
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 24) || 'task';
  const randomPart = globalThis.crypto?.randomUUID?.().replaceAll('-', '')
    || `${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
  return `${safeScope}-${Date.now().toString(36)}-${randomPart.slice(0, 20)}`.slice(0, 64);
};

const recordValue = (record, camelKey, snakeKey) => record?.[camelKey] ?? record?.[snakeKey];

const pollingTaskFromRecord = (record) => {
  if (!record) return null;
  const internalTaskId = String(recordValue(record, 'internalTaskId', 'internal_task_id') || '');
  const providerTaskId = String(recordValue(record, 'providerTaskId', 'provider_task_id') || '');
  const taskId = providerTaskId || internalTaskId;
  if (!taskId) return null;
  return {
    taskId,
    internalTaskId: internalTaskId || taskId,
    taskType: record.taskType || record.managedTaskKind,
    status: record.status || 'processing',
    source: record.source,
    recoveredRecord: record,
  };
};

/**
 * 提交返回丢失时，只在指定项目 + 完全相同 source 中找原任务。
 * requestId 存在时必须精确命中 internalTaskId，不会误恢复同 source 的旧任务。
 */
export const findLatestTaskByExactSource = async ({
  projectId,
  source,
  requestId = '',
  attempts = 3,
  intervalMs = 600,
  signal,
} = {}) => {
  if (!projectId || !source) return null;
  const expectedRequestId = String(requestId || '');
  const safeAttempts = Math.max(1, Number(attempts) || 1);

  for (let attempt = 0; attempt < safeAttempts; attempt += 1) {
    throwIfTaskAborted(signal);
    let tasks;
    try {
      tasks = await authenticatedFetch(
        `${API_HOST}/api/ai-generation-tasks?projectId=${encodeURIComponent(projectId)}&source=${encodeURIComponent(source)}&limit=10`
      );
    } catch (error) {
      throwIfTaskAborted(signal);
      throw error;
    }
    throwIfTaskAborted(signal);
    const exactTasks = (Array.isArray(tasks) ? tasks : [])
      .filter((record) => String(record?.source || '') === String(source));
    const matched = expectedRequestId
      ? exactTasks.find((record) => String(recordValue(record, 'internalTaskId', 'internal_task_id') || '') === expectedRequestId)
      : exactTasks[0];
    if (matched) return pollingTaskFromRecord(matched);

    if (attempt + 1 < safeAttempts) {
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
      throwIfTaskAborted(signal);
    }
  }
  return null;
};

const pollTaskToCompletion = async (taskOrId, options = {}) => {
  const task = normalizePollingTask(taskOrId, options);
  if (!task.taskId) {
    throw new Error('缺少需要查询的任务ID');
  }
  const startedAt = Date.now();
  const timeoutMs = options.timeoutMs || 30 * 60 * 1000;
  const initialIntervalMs = options.intervalMs || 3000;

  return await new Promise((resolve, reject) => {
    let stopped = false;
    let timer = null;
    let consecutiveErrors = 0;
    const stop = () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      options.signal?.removeEventListener?.('abort', onAbort);
    };

    const onAbort = () => {
      if (stopped) return;
      stop();
      reject(makeTaskAbortError());
    };

    const poll = async () => {
      if (stopped) return;
      if (options.signal?.aborted) {
        onAbort();
        return;
      }
      if (Date.now() - startedAt > timeoutMs) {
        stop();
        // 客户端停止等待不等于后台生成失败；保留任务 ID 供稍后续查。
        const timeoutError = new Error(`已停止长时间等待，后台任务可能仍在执行：${task.taskId}`);
        timeoutError.taskId = task.taskId;
        timeoutError.internalTaskId = task.internalTaskId || task.taskId;
        timeoutError.pollingInterrupted = true;
        reject(timeoutError);
        return;
      }
      try {
        const status = await queryTaskStatus(task.taskId);
        // fetch 本身使用内部超时 controller，因此外部 abort 可能在 await 期间发生。
        // 每次 await 后都必须再检查，防止旧轮询继续回写 UI 或安排定时器。
        if (stopped || options.signal?.aborted) return;
        // 响应防御：响应体不是对象或缺少 status 字段时按异常计数，避免静默死循环
        if (!status || typeof status !== 'object' || !('status' in status)) {
          throw new Error('任务状态响应异常（缺少 status 字段）');
        }
        consecutiveErrors = 0; // 查询成功，重置连续错误计数
        let visibleStatus = withTaskIdentity(status, task);
        if (typeof options.queryDetailedProgress === 'function') {
          try {
            const detailed = await options.queryDetailedProgress(task, status);
            if (stopped || options.signal?.aborted) return;
            if (detailed && typeof detailed === 'object') {
              // 终态仍以通用任务状态为准；专用接口只补充真实阶段、段数和百分比。
              visibleStatus = withTaskIdentity({
                ...status,
                ...detailed,
                transportStatus: status.status,
              }, task);
            }
          } catch (detailError) {
            if (stopped || options.signal?.aborted) return;
            options.onDetailedProgressError?.({
              taskId: task.taskId,
              internalTaskId: task.internalTaskId,
              elapsedMs: Date.now() - startedAt,
              error: detailError,
            });
          }
        }
        if (stopped || options.signal?.aborted) return;
        options.onProgress?.(visibleStatus);
        if (stopped || options.signal?.aborted) return;
        if (status.status === 'success') {
          stop();
          const completion = normalizeTaskCompletionResult(taskStatusRawResult(status));
          resolve({
            taskId: task.taskId,
            internalTaskId: task.internalTaskId || task.taskId,
            submittedTask: task,
            ...completion,
          });
          return;
        }
        if (status.status === 'failed' || status.status === 'not_found' || status.status === 'canceled') {
          stop();
          reject(createTerminalTaskError(status, task));
          return;
        }
        const elapsed = Date.now() - startedAt;
        const interval = elapsed < 30000 ? initialIntervalMs : elapsed < 120000 ? 8000 : 15000;
        if (!stopped && !options.signal?.aborted) timer = setTimeout(poll, interval);
      } catch (error) {
        if (stopped || options.signal?.aborted) return;
        // 查询异常不再静默吞掉：计数 + 通知调用方，连续超过 20 次直接失败
        consecutiveErrors++;
        const elapsedMs = Date.now() - startedAt;
        try {
          options.onPollError?.({
            consecutiveErrors,
            elapsedMs,
            error,
            taskId: task.taskId,
            internalTaskId: task.internalTaskId || task.taskId,
          });
        } catch (callbackError) {
          console.warn('[TaskService] onPollError 回调异常:', callbackError);
        }
        if (stopped || options.signal?.aborted) return;
        if (consecutiveErrors > 20) {
          stop();
          const pollingError = new Error('网络连接持续异常，已停止等待任务');
          pollingError.taskId = task.taskId;
          pollingError.internalTaskId = task.internalTaskId || task.taskId;
          pollingError.pollingInterrupted = true;
          reject(pollingError);
          return;
        }
        const interval = elapsedMs < 120000 ? 8000 : 15000;
        if (!stopped && !options.signal?.aborted) timer = setTimeout(poll, interval);
      }
    };

    if (options.signal?.aborted) {
      onAbort();
      return;
    }
    options.signal?.addEventListener?.('abort', onAbort, { once: true });
    poll();
  });
};

export const runTaskToCompletion = async (taskType, payload, options = {}) => {
  throwIfTaskAborted(options.signal);
  let submitted;
  let submissionError = null;
  try {
    submitted = await submitTask(taskType, payload, options);
  } catch (error) {
    submissionError = error;
    throwIfTaskAborted(options.signal);
    if (typeof options.recoverSubmittedTask !== 'function') throw error;
    try {
      submitted = await options.recoverSubmittedTask({
        error,
        requestId: options.requestId || '',
        projectId: options.projectId,
        source: options.source,
      });
    } catch (recoveryError) {
      throwIfTaskAborted(options.signal);
      if (options.requestId) {
        throw makeSubmissionUncertainError(options.requestId, recoveryError);
      }
      throw recoveryError;
    }
    throwIfTaskAborted(options.signal);
    if (!submitted?.taskId) {
      if (options.requestId) throw makeSubmissionUncertainError(options.requestId, error);
      throw error;
    }
  }
  // POST 已经返回也可能恰好遇到页面卸载，此时不能再触发回调或进入轮询。
  throwIfTaskAborted(options.signal);
  const task = normalizePollingTask(submitted, options);
  if (submissionError) {
    await options.onSubmissionRecovered?.(task, submissionError);
    throwIfTaskAborted(options.signal);
  }
  try {
    await options.onSubmitted?.(task);
  } catch (callbackError) {
    throwIfTaskAborted(options.signal);
    throw callbackError;
  }
  throwIfTaskAborted(options.signal);
  return pollTaskToCompletion(task, options);
};

/**
 * 恢复一个已经提交的任务，只继续查询，不会再次创建 AI 任务。
 */
export const resumeTaskToCompletion = async (taskId, options = {}) => {
  throwIfTaskAborted(options.signal);
  const task = normalizePollingTask(taskId, options);
  try {
    await options.onResumed?.(task);
  } catch (callbackError) {
    throwIfTaskAborted(options.signal);
    throw callbackError;
  }
  throwIfTaskAborted(options.signal);
  return pollTaskToCompletion(task, options);
};

export const runImageTaskToUrl = async (taskType, payload, options = {}) => {
  const imageTaskOptions = options.provider
    ? options
    : { ...options, provider: getCurrentImageProvider() };
  try {
    const { url, taskId } = await runTaskToCompletion(taskType, payload, imageTaskOptions);
    if (!url) {
      throw new Error(`任务完成但没有返回图片地址：${taskId}`);
    }
    return url;
  } catch (error) {
    // A provider may report failed after the generated image has already been
    // persisted to COS. Only the image-specific convenience API treats that URL
    // as recoverable; generic video/text callers still receive the terminal error.
    const recoveredUrl = extractRecoverableTaskResultUrl(error);
    if (recoveredUrl) return recoveredUrl;
    throw error;
  }
};

/**
 * 查询任务状态
 * @param {string} taskId - 任务ID
 * @returns {Promise<{taskId, status, taskType, result, errorMessage, retryCount}>}
 */
export const queryTaskStatus = async (taskId) => {
  const response = await authenticatedFetch(
    `${API_HOST}/api/task/status?taskId=${taskId}`
  );
  return response;
};

/**
 * 通过业务请求号恢复任务。用于提交响应丢失、页面刷新等场景，
 * 避免为了找回 taskId 再次创建昂贵的 AI 任务。
 */
export const queryTaskStatusByRequestId = async (requestId) => {
  const response = await authenticatedFetch(
    `${API_HOST}/api/task/status/by-request-id?requestId=${encodeURIComponent(requestId)}`
  );
  return response;
};

/**
 * 查询图片任务状态（通过 shotId 从数据库读取）
 * @param {number} shotId - 分镜ID
 * @returns {Promise<{shotId, status, generatedImage, imageCandidates}>}
 */
export const queryImageTaskStatus = async (shotId) => {
  const response = await authenticatedFetch(
    `${API_HOST}/api/task/image-status?shotId=${shotId}`
  );
  return response;
};

/**
 * 开始轮询任务状态
 * @param {string} taskId - 任务ID
 * @param {Object} options - 配置
 * @param {Function} options.onSuccess - 成功回调 (result)
 * @param {Function} options.onError - 失败回调 (errorMessage)
 * @param {Function} options.onPollingInterrupted - 仅轮询链路中断时的回调，不代表后台任务失败
 * @param {Function} options.onProgress - 进度回调 ({status, retryCount, currentStage, currentStageText, progress})
 * @param {number} options.interval - 轮询间隔（毫秒），默认 3000
 * @param {number} options.maxTotalMs - 总等待时长上限（毫秒），默认 45 分钟，超时触发 onError
 * @returns {Function} stop - 调用此函数停止轮询
 */
/**
 * 渐进式退避：前30秒密集轮询，之后逐步降频
 * 图片生成耗时长达5分钟，没必要全程3秒一次
 */
const getAdaptiveInterval = (startTime, baseInterval) => {
  const elapsed = Date.now() - startTime;
  if (elapsed < 30000) return baseInterval;   // 前30秒：原始间隔（3s）
  if (elapsed < 120000) return 8000;          // 30s-2min：8s
  return 15000;                                // 2min+：15s
};

export const startTaskPolling = (taskId, options = {}) => {
  const {
    onSuccess,
    onError,
    onPollingInterrupted,
    onProgress,
    interval = 3000,
    maxTotalMs = 45 * 60 * 1000, // 总时长上限：默认 45 分钟，防止无限轮询
  } = options;

  const notifyPollingInterrupted = (message, reason) => {
    const callback = onPollingInterrupted || onError;
    callback?.(message, {
      reason,
      taskId,
      elapsedMs: Date.now() - startTime,
      consecutiveErrors: errorCount,
    });
  };

  let isRunning = true;
  let timeoutId = null;
  let errorCount = 0;
  const startTime = Date.now();

  // 失败类终态及默认人话文案（优先使用后端返回的 message/errorMessage）
  const ERROR_STATUS_DEFAULT_MESSAGES = {
    failed: '任务执行失败',
    not_found: '任务不存在或已过期',
    canceled: '任务已取消',
    cancelled: '任务已取消',
    stopped: '任务已被停止',
    timeout: '任务执行超时',
    error: '任务执行出错',
  };

  const poll = async () => {
    if (!isRunning) return;

    // 总时长上限：任何分支都不能无限轮询
    if (Date.now() - startTime > maxTotalMs) {
      isRunning = false;
      notifyPollingInterrupted('任务仍可能在后台执行，已暂停本轮查询', 'polling_timeout');
      return;
    }

    try {
      const data = await queryTaskStatus(taskId);
      if (!isRunning) return;

      // 响应防御：解包后的 data 不是对象或缺少 status 字段时按"未知响应"计入连续异常，
      // 与网络错误共用同一计数器，避免静默死循环
      if (!data || typeof data !== 'object' || !('status' in data)) {
        errorCount++;
        console.debug(`[TaskService] 未知响应 taskId=${taskId} waited=${Math.round((Date.now() - startTime) / 1000)}s consecutiveErrors=${errorCount}`, data);
        if (errorCount >= 15) {
          isRunning = false;
          notifyPollingInterrupted('服务响应暂时异常，稍后将继续查询同一任务', 'invalid_status_response');
        } else {
          const backoff = Math.min(interval * errorCount, 30000);
          if (isRunning) timeoutId = setTimeout(poll, backoff);
        }
        return;
      }

      errorCount = 0; // 重置网络错误计数
      console.debug(`[TaskService] 轮询 taskId=${taskId} status=${data.status} waited=${Math.round((Date.now() - startTime) / 1000)}s consecutiveErrors=0`);

      if (data.status === 'success') {
        isRunning = false;
        const completion = normalizeTaskCompletionResult(taskStatusRawResult(data));
        onSuccess?.(completion.result, {
          taskId,
          rawResult: completion.rawResult,
          url: completion.url,
        });
      } else if (Object.prototype.hasOwnProperty.call(ERROR_STATUS_DEFAULT_MESSAGES, data.status)) {
        isRunning = false;
        onError?.(data.errorMessage || data.message || ERROR_STATUS_DEFAULT_MESSAGES[data.status]);
      } else {
        // processing / 未知非终态 status - 继续轮询，传递完整的状态信息（受总时长上限约束）
        onProgress?.({
          status: data.status,
          retryCount: data.retryCount,
          currentStage: data.currentStage,
          currentStageText: data.currentStageText,
          progress: data.progress
        });
        const nextInterval = getAdaptiveInterval(startTime, interval);
        if (isRunning) timeoutId = setTimeout(poll, nextInterval);
      }
    } catch (e) {
      if (!isRunning) return;
      console.error('[TaskService] 轮询失败:', e);
      errorCount++;
      if (errorCount >= 15) {
        isRunning = false;
        notifyPollingInterrupted('网络连接暂时中断，稍后将继续查询同一任务', 'network_interrupted');
      } else {
        // 指数退避：3s, 6s, 9s, 12s... 最大30s
        const backoff = Math.min(interval * errorCount, 30000);
        console.warn(`[TaskService] 第${errorCount}次重试，${backoff/1000}s后重试`);
        if (isRunning) timeoutId = setTimeout(poll, backoff);
      }
    }
  };

  // 首次轮询
  poll();

  // 返回停止函数
  return () => {
    isRunning = false;
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  };
};
